import type { ExcelGridColumn, ExcelGridRow } from "../../shared/excel-grid/models/excel-grid.models";
import { ADMIN_CERTIFICATES_KPIS_ENVIRONMENT_OPTIONS } from "../models/admin-kpis.models";
import type {
  AdminCertificatesKpisChangedNameRow,
  AdminCertificatesKpisChartSegment,
  AdminCertificatesKpisDailySnapshotTrendPoint,
  AdminCertificatesKpisDocumentRow,
  AdminCertificatesKpisMissingCertificateRow,
  AdminCertificatesKpisResponse,
  AdminKpisMissingCertificatesTableFilter,
  AdminKpisFilters,
  AdminKpisTrendChartData,
  AdminKpisTrendPoint,
  AdminKpisTrendWindow,
  AdminKpisTrendWindowOption,
} from "../models/admin-kpis.models";
import { createElement } from "react";

const ADMIN_KPIS_PRINT_BODY_CLASS = "admin-kpis-printing";
const ADMIN_KPIS_PRINT_PAGE_STYLE_ID = "admin-kpis-print-page-style";
const ADMIN_KPIS_PRINT_PAGE_STYLE = `
@page {
  size: A4 landscape;
  margin: 10mm;
}
`;

export const PIE_CHART_CENTER = 50;
export const PIE_CHART_RADIUS = 49.5;

export const EMPTY_ADMIN_CERTIFICATES_KPIS_RESPONSE: AdminCertificatesKpisResponse = {
  updatedAt: "",
  actualEnvironment: "DEV",
  selectedEnvironment: "DEV",
  environmentOptions: ADMIN_CERTIFICATES_KPIS_ENVIRONMENT_OPTIONS,
  sourceVesselTypeOptions: [],
  defaultExcludedVesselTypes: [],
  selectedVesselTypes: [],
  filterOptions: {
    inFleet: [true, false],
    contractTypes: [],
    vesselNames: [],
    vesselTypes: [],
    documentTypes: [],
    documentNames: [],
    alerts: [],
  },
  documents: [],
  missingCertificates: [],
  periodFleetCertificateStatus: { total: 0, segments: [], kpis: [] },
  periodFleetBalticQuestionnaireStatus: { total: 0, segments: [], kpis: [] },
  dailySnapshotHistory: [],
  periodTcChangedNames: [],
};

export const ADMIN_KPIS_FILTER_OPTION_ROW_HEIGHT = 32;
export const ADMIN_KPIS_FILTER_OPTION_LIST_MAX_HEIGHT = 256;

export const ADMIN_KPIS_TABLE_ACTION_BUTTON_CLASS_NAME =
  "font-semibold text-[#367C2B] no-underline transition-colors hover:text-[#285F20] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#367C2B] dark:text-green";

export function getAdminKpisAriaExpandedProps(isExpanded: boolean): { "aria-expanded": "true" | "false" } {
  return { "aria-expanded": isExpanded ? "true" : "false" };
}

export const DEFAULT_ADMIN_KPIS_FILTERS: AdminKpisFilters = {
  inFleet: true,
  contractTypes: [],
  vesselNames: [],
  vesselTypes: [],
  documentTypes: [],
  documentNames: [],
  expirationDateBefore: "",
  surveyDateBefore: "",
  openDateBefore: "",
  alerts: [],
};

export const ADMIN_KPIS_MISSING_CERTIFICATES_COLUMNS: ExcelGridColumn[] = [
  gridColumn("name", "Name", 190),
  gridColumn("open", "Open", 110),
  gridColumn("imo", "IMO", 110),
  gridColumn("code", "Code", 100),
  gridColumn("type", "Type", 130),
  gridColumn("contract", "Contract", 120),
  gridColumn("alerts", "Alerts", 90),
  gridColumn("notices", "Notices", 90),
  gridColumn("missing", "Missing", 95),
  gridColumn("availableDocuments", "Available Documents", 260),
  gridColumn("availableCount", "A", 70),
  gridColumn("missingDocuments", "Missing Documents", 260),
  gridColumn("missingCount", "M", 70),
];

export const ADMIN_KPIS_CHANGED_NAME_COLUMNS: ExcelGridColumn[] = [
  gridColumn("code", "Code", 110),
  gridColumn("currentName", "Current Name", 220),
  gridColumn("oldNames", "Old Names", 360),
];

export const ADMIN_KPIS_DOCUMENT_COLUMNS: ExcelGridColumn[] = [
  gridColumn("vesselName", "Vessel Name", 190),
  gridColumn("openDate", "Open Date", 110),
  gridColumn("imoNumber", "IMO Number", 120),
  gridColumn("vesselCode", "Vessel Code", 115),
  gridColumn("vesselType", "Vessel Type", 140),
  gridColumn("documentType", "Document Type", 150),
  gridColumn("documentName", "Document Name", 220),
  gridColumn("source", "Source", 120),
  gridColumn("status", "Status", 110),
  gridColumn("createdAt", "Created At", 110),
  gridColumn("bqStatus", "BQ status", 120),
  gridColumn("bqSubmittedOn", "BQ submitted on", 135),
  gridColumn("expiresOn", "Expires On", 110),
  gridColumn("expiryNotApplicable", "Expiry not applicable", 160),
  gridColumn("nextSurveyDate", "Next Survey Date", 145),
  gridColumn("alert", "Alerts", 230),
  gridColumn("inFleet", "In Fleet", 95),
  gridColumn("portOfIssueIsWhoApproved", "WHO Approved", 125),
  gridColumn("deliveryDate", "Delivery Date", 120),
  gridColumn("redeliveryDate", "Redelivery Date", 130),
  gridColumn("minRedeliveryDate", "Min Redelivery Date", 160),
  gridColumn("maxRedeliveryDate", "Max Redelivery Date", 160),
  gridColumn("contractType", "Contract Type", 130),
];

export type AdminKpisPieSlice = {
  key: string;
  className: string;
  pathData: string;
};

export type AdminKpisPiePercentLabel = {
  key: string;
  text: string;
  x: number;
  y: number;
};

export const DEFAULT_ADMIN_KPIS_TREND_WINDOW: AdminKpisTrendWindow = "30d";

export const ADMIN_KPIS_TREND_WINDOW_OPTIONS: AdminKpisTrendWindowOption[] = [
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "180d", label: "180 days" },
  { value: "360d", label: "360 days" },
];

export const ADMIN_KPIS_TREND_CHART_WIDTH = 640;
export const ADMIN_KPIS_PRINT_TREND_CHART_WIDTH = 1320;
export const ADMIN_KPIS_TREND_CHART_HEIGHT = 220;
export const ADMIN_KPIS_TREND_PLOT_LEFT = 46;
export const ADMIN_KPIS_TREND_PLOT_RIGHT = 18;
export const ADMIN_KPIS_TREND_PLOT_TOP = 20;
export const ADMIN_KPIS_TREND_PLOT_BOTTOM = 42;
export const ADMIN_KPIS_TREND_MIN_CHART_WIDTH = ADMIN_KPIS_TREND_PLOT_LEFT + ADMIN_KPIS_TREND_PLOT_RIGHT + 1;

export function filterAdminKpisDocuments(
  rows: AdminCertificatesKpisDocumentRow[],
  filters: AdminKpisFilters,
): AdminCertificatesKpisDocumentRow[] {
  return rows.filter((row) => {
    if (row.inFleet !== filters.inFleet) {
      return false;
    }

    if (filters.contractTypes.length > 0 && !filters.contractTypes.includes(row.contractType)) {
      return false;
    }

    if (filters.vesselNames.length > 0 && !filters.vesselNames.includes(row.vesselName)) {
      return false;
    }

    if (filters.vesselTypes.length > 0 && !filters.vesselTypes.includes(row.vesselType)) {
      return false;
    }

    if (filters.documentTypes.length > 0 && !filters.documentTypes.includes(row.documentType)) {
      return false;
    }

    if (filters.documentNames.length > 0 && !filters.documentNames.includes(row.documentName)) {
      return false;
    }

    if (filters.alerts.length > 0 && (!row.alert || !filters.alerts.includes(row.alert))) {
      return false;
    }

    if (filters.expirationDateBefore && !isBeforeDate(row.expiresOn, filters.expirationDateBefore)) {
      return false;
    }

    if (filters.surveyDateBefore && !isBeforeDate(row.nextSurveyDate, filters.surveyDateBefore)) {
      return false;
    }

    if (filters.openDateBefore && !isBeforeDate(row.openDate, filters.openDateBefore)) {
      return false;
    }

    return true;
  });
}

export function countVesselsWithDocumentAlert(rows: AdminCertificatesKpisDocumentRow[]): number {
  return new Set(rows.filter((row) => String(row.alert ?? "").trim() !== "").map((row) => row.vesselName)).size;
}

export function toAdminKpisDocumentGridRows(rows: AdminCertificatesKpisDocumentRow[]): ExcelGridRow[] {
  return rows.map((row) => ({
    ...row,
    alert: row.alert ?? "",
    openDate: row.openDate ?? "",
    createdAt: row.createdAt ?? "",
    bqSubmittedOn: row.bqSubmittedOn ?? "",
    expiresOn: row.expiresOn ?? "",
    nextSurveyDate: row.nextSurveyDate ?? "",
    portOfIssueIsWhoApproved: row.portOfIssueIsWhoApproved ?? "",
    deliveryDate: row.deliveryDate ?? "",
    redeliveryDate: row.redeliveryDate ?? "",
    minRedeliveryDate: row.minRedeliveryDate ?? "",
    maxRedeliveryDate: row.maxRedeliveryDate ?? "",
  }));
}

export function toAdminKpisMissingCertificateGridRows(
  rows: AdminCertificatesKpisMissingCertificateRow[],
): ExcelGridRow[] {
  return rows.map((row) => ({
    ...row,
    open: row.open ?? "",
  }));
}

export function toAdminKpisChangedNameGridRows(rows: AdminCertificatesKpisChangedNameRow[]): ExcelGridRow[] {
  return rows.map((row) => ({ ...row }));
}

export function filterMissingCertificatesByLegend(
  rows: AdminCertificatesKpisMissingCertificateRow[],
  activeFilter: AdminKpisMissingCertificatesTableFilter | null,
): AdminCertificatesKpisMissingCertificateRow[] {
  if (!activeFilter) {
    return rows;
  }

  if (activeFilter.source === "periodCertificate") {
    return rows.filter((row) => row.periodCertificateStatusCode === activeFilter.code);
  }

  return rows.filter((row) => row.periodBalticQuestionnaireStatusCode === activeFilter.code);
}

export function getMissingCertificateCountLabel(
  activeFilter: AdminKpisMissingCertificatesTableFilter | null,
  visibleRowCount: number,
  response: AdminCertificatesKpisResponse,
): string {
  if (!activeFilter) {
    return `${response.missingCertificates.length} vessels checked`;
  }

  const total =
    activeFilter.source === "periodCertificate"
      ? response.periodFleetCertificateStatus.total
      : response.periodFleetBalticQuestionnaireStatus.total;

  return `${visibleRowCount} of ${total} vessels checked`;
}

export function getMissingCertificateColumnsWithTruncatedDocumentLists(): ExcelGridColumn[] {
  return ADMIN_KPIS_MISSING_CERTIFICATES_COLUMNS.map((column) => {
    if (column.key !== "availableDocuments" && column.key !== "missingDocuments") {
      return column;
    }

    return {
      ...column,
      renderCell: (row: ExcelGridRow) => {
        const value = String(row[column.key] ?? "");

        return createElement("span", { className: "block max-w-full truncate whitespace-nowrap", title: value }, value);
      },
    };
  });
}

export function buildPieSlices(segments: AdminCertificatesKpisChartSegment[], total: number): AdminKpisPieSlice[] {
  if (total <= 0) {
    return [];
  }

  let offsetDegrees = 0;

  return segments.flatMap((segment) => {
    const segmentValue = Math.max(segment.value, 0);
    const startDegrees = offsetDegrees;
    const valueDegrees = (segmentValue / total) * 360;
    const endDegrees = startDegrees + valueDegrees;
    offsetDegrees = endDegrees;

    if (segmentValue <= 0) {
      return [];
    }

    return [
      {
        key: segment.code,
        className: `admin-kpis-pie-chart-segment ${getAdminKpisPieChartSegmentColorClassName(segment.code)}`,
        pathData:
          valueDegrees >= 360 ? buildFullPieSlicePath() : buildPieSlicePath(startDegrees, endDegrees, valueDegrees),
      },
    ];
  });
}

export function buildPiePercentLabels(
  segments: AdminCertificatesKpisChartSegment[],
  total: number,
): AdminKpisPiePercentLabel[] {
  if (total <= 0) {
    return [];
  }

  let offset = 0;
  const radiusPercent = 32;

  return segments.flatMap((segment) => {
    const start = offset;
    const valueDegrees = (segment.value / total) * 360;
    const end = start + valueDegrees;
    offset = end;

    if (segment.value <= 0) {
      return [];
    }

    const midpointRadians = ((start + valueDegrees / 2) * Math.PI) / 180;
    const left = 50 + Math.sin(midpointRadians) * radiusPercent;
    const top = 50 - Math.cos(midpointRadians) * radiusPercent;

    return [
      {
        key: segment.code,
        text: `${((segment.value / total) * 100).toFixed(1)}%`,
        x: left,
        y: top,
      },
    ];
  });
}

export function buildPieAriaLabel(segments: AdminCertificatesKpisChartSegment[], total: number): string {
  if (total <= 0) {
    return "0 vessels";
  }

  const segmentSummary = segments
    .filter((segment) => segment.value > 0)
    .map((segment) => `${segment.code} ${((segment.value / total) * 100).toFixed(1)}%`)
    .join(", ");

  return segmentSummary ? `${total} vessels: ${segmentSummary}` : `${total} vessels`;
}

export function getAdminKpisPieChartSegmentColorClassName(segmentCode: string): string {
  switch (segmentCode) {
    case "A":
      return "admin-kpis-pie-chart-segment-a";
    case "A1":
      return "admin-kpis-pie-chart-segment-a1";
    case "A2":
      return "admin-kpis-pie-chart-segment-a2";
    case "B1":
      return "admin-kpis-pie-chart-segment-b1";
    case "B2":
      return "admin-kpis-pie-chart-segment-b2";
    case "B3":
      return "admin-kpis-pie-chart-segment-b3";
    default:
      return "admin-kpis-pie-chart-segment-default";
  }
}

export function getAdminKpisSegmentSwatchColorClassName(segmentCode: string): string {
  switch (segmentCode) {
    case "A":
      return "admin-kpis-pie-legend-swatch-a";
    case "A1":
      return "admin-kpis-pie-legend-swatch-a1";
    case "A2":
      return "admin-kpis-pie-legend-swatch-a2";
    case "B1":
      return "admin-kpis-pie-legend-swatch-b1";
    case "B2":
      return "admin-kpis-pie-legend-swatch-b2";
    case "B3":
      return "admin-kpis-pie-legend-swatch-b3";
    default:
      return "admin-kpis-pie-legend-swatch-default";
  }
}

export function getAdminKpisTrendColorClassName(segmentCode: string): string {
  switch (segmentCode) {
    case "A":
      return "admin-kpis-trend-color-a";
    case "A1":
      return "admin-kpis-trend-color-a1";
    case "A2":
      return "admin-kpis-trend-color-a2";
    case "B1":
      return "admin-kpis-trend-color-b1";
    case "B2":
      return "admin-kpis-trend-color-b2";
    case "B3":
      return "admin-kpis-trend-color-b3";
    default:
      return "admin-kpis-trend-color-default";
  }
}

export function getAdminKpisPressedButtonAriaProps(isPressed: boolean): { "aria-pressed": "true" | "false" } {
  return { "aria-pressed": isPressed ? "true" : "false" };
}

export function getAdminKpisTrendSegment(
  activeFilter: AdminKpisMissingCertificatesTableFilter | null,
  periodFleetCertificateStatus: AdminCertificatesKpisResponse["periodFleetCertificateStatus"],
  periodFleetBalticQuestionnaireStatus: AdminCertificatesKpisResponse["periodFleetBalticQuestionnaireStatus"],
): AdminCertificatesKpisChartSegment | null {
  if (!activeFilter) {
    return null;
  }

  const segments =
    activeFilter.source === "periodCertificate"
      ? periodFleetCertificateStatus.segments
      : periodFleetBalticQuestionnaireStatus.segments;

  return segments.find((segment) => segment.code === activeFilter.code) ?? null;
}

export function getAdminKpisTrendPoints(
  history: AdminCertificatesKpisDailySnapshotTrendPoint[],
  activeFilter: AdminKpisMissingCertificatesTableFilter | null,
  selectedWindow: AdminKpisTrendWindow,
): AdminKpisTrendPoint[] {
  if (!activeFilter || history.length === 0) {
    return [];
  }

  const sortedHistory = [...history].sort((first, second) => first.snapshotDate.localeCompare(second.snapshotDate));
  const latestSnapshotDate = sortedHistory[sortedHistory.length - 1]?.snapshotDate;
  const startDate = getAdminKpisTrendWindowStartDate(latestSnapshotDate, selectedWindow);

  return sortedHistory
    .filter((point) => point.snapshotDate >= startDate)
    .map((point) => {
      const metric =
        activeFilter.source === "periodCertificate"
          ? point.periodCertificateStatus[activeFilter.code]
          : point.periodBalticQuestionnaireStatus[activeFilter.code];

      return {
        snapshotDate: point.snapshotDate,
        count: metric.count,
        percentage: metric.percentage,
        periodFleetVesselsTotal: point.periodFleetVesselsTotal,
      };
    });
}

export function buildAdminKpisTrendChartData(
  points: AdminKpisTrendPoint[],
  requestedChartWidth = ADMIN_KPIS_TREND_CHART_WIDTH,
  selectedWindow: AdminKpisTrendWindow = DEFAULT_ADMIN_KPIS_TREND_WINDOW,
): AdminKpisTrendChartData {
  const chartWidth = getAdminKpisTrendChartWidth(requestedChartWidth);

  if (points.length === 0) {
    return {
      chartWidth,
      points: [],
      tooltipPoints: [],
      dateAxisPoints: [],
      pathData: "",
      maxCount: 0,
      midpointCount: 0,
      firstDateLabel: "",
      lastDateLabel: "",
      latestPoint: null,
    };
  }

  const plotWidth = chartWidth - ADMIN_KPIS_TREND_PLOT_LEFT - ADMIN_KPIS_TREND_PLOT_RIGHT;
  const plotHeight = ADMIN_KPIS_TREND_CHART_HEIGHT - ADMIN_KPIS_TREND_PLOT_TOP - ADMIN_KPIS_TREND_PLOT_BOTTOM;
  const maxCount = Math.max(...points.map((point) => point.count), 0);
  const scaleMax = Math.max(maxCount, 1);
  const firstSnapshotTime = getSnapshotDateTime(points[0].snapshotDate);
  const lastSnapshotTime = getSnapshotDateTime(points[points.length - 1].snapshotDate);
  const chartPoints = points.map((point, index) => {
    const x = getAdminKpisTrendPointX(
      point.snapshotDate,
      index,
      points.length,
      plotWidth,
      firstSnapshotTime,
      lastSnapshotTime,
    );
    const y = ADMIN_KPIS_TREND_PLOT_TOP + (1 - point.count / scaleMax) * plotHeight;

    return {
      ...point,
      x: roundChartCoordinate(x),
      y: roundChartCoordinate(y),
    };
  });
  const tooltipPoints = getAdminKpisTrendTooltipPoints(chartPoints, selectedWindow);

  return {
    chartWidth,
    points: chartPoints,
    tooltipPoints,
    dateAxisPoints: getAdminKpisTrendDateAxisPoints(tooltipPoints),
    pathData: buildSmoothTrendPath(chartPoints),
    maxCount,
    midpointCount: Math.round(maxCount / 2),
    firstDateLabel: formatAdminKpisTrendDate(points[0].snapshotDate),
    lastDateLabel: formatAdminKpisTrendDate(points[points.length - 1].snapshotDate),
    latestPoint: points[points.length - 1],
  };
}

export function formatAdminKpisTrendDate(snapshotDate: string): string {
  const date = parseSnapshotDate(snapshotDate);

  if (!date) {
    return snapshotDate;
  }

  return new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit", timeZone: "UTC" }).format(date);
}

export function formatAdminKpisTrendPointTitle(point: AdminKpisTrendPoint): string {
  return `${formatAdminKpisTrendDate(point.snapshotDate)}: ${point.count} vessels (${point.percentage.toFixed(1)}%)`;
}

export function getAdminKpisTrendDateLabelClassName(index: number, labelCount: number): string {
  if (index === labelCount - 1) {
    return "admin-kpis-trend-date-label admin-kpis-trend-date-label-end";
  }

  if (index > 0) {
    return "admin-kpis-trend-date-label admin-kpis-trend-date-label-middle";
  }

  return "admin-kpis-trend-date-label";
}

/**
 * Owns the admin KPI print-mode side effect so the panel caller does not need
 * to know about body classes, print-only page sizing, or cleanup timing.
 */
export function printAdminKpisPdfExport(): void {
  const printPageStyle = createAdminKpisPrintPageStyle();
  let cleanupTimeoutId: number | undefined;
  let cleanupCompleted = false;

  const cleanupPrintMode = () => {
    if (cleanupCompleted) {
      return;
    }

    cleanupCompleted = true;
    window.removeEventListener("afterprint", cleanupPrintMode);
    if (cleanupTimeoutId !== undefined) {
      window.clearTimeout(cleanupTimeoutId);
    }
    document.body.classList.remove(ADMIN_KPIS_PRINT_BODY_CLASS);
    printPageStyle.remove();
  };

  document.body.classList.add(ADMIN_KPIS_PRINT_BODY_CLASS);
  window.addEventListener("afterprint", cleanupPrintMode, { once: true });

  try {
    window.print();
  } catch (error) {
    cleanupPrintMode();
    throw error;
  }

  if (!cleanupCompleted) {
    cleanupTimeoutId = window.setTimeout(cleanupPrintMode, 60000);
  }
}

function gridColumn(key: string, label: string, minWidth: number): ExcelGridColumn {
  return {
    key,
    label,
    minWidth,
    editable: false,
  };
}

function isBeforeDate(value: string | null, dateBefore: string): boolean {
  return Boolean(value && value < dateBefore);
}

function buildFullPieSlicePath(): string {
  return [
    `M ${PIE_CHART_CENTER} ${PIE_CHART_CENTER - PIE_CHART_RADIUS}`,
    `A ${PIE_CHART_RADIUS} ${PIE_CHART_RADIUS} 0 1 1 ${PIE_CHART_CENTER} ${PIE_CHART_CENTER + PIE_CHART_RADIUS}`,
    `A ${PIE_CHART_RADIUS} ${PIE_CHART_RADIUS} 0 1 1 ${PIE_CHART_CENTER} ${PIE_CHART_CENTER - PIE_CHART_RADIUS}`,
    "Z",
  ].join(" ");
}

function buildPieSlicePath(startDegrees: number, endDegrees: number, valueDegrees: number): string {
  const start = getPieSlicePoint(startDegrees);
  const end = getPieSlicePoint(endDegrees);
  const largeArcFlag = valueDegrees > 180 ? 1 : 0;

  return [
    `M ${PIE_CHART_CENTER} ${PIE_CHART_CENTER}`,
    `L ${start.x} ${start.y}`,
    `A ${PIE_CHART_RADIUS} ${PIE_CHART_RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
}

function getPieSlicePoint(degrees: number): { x: number; y: number } {
  const radians = (degrees * Math.PI) / 180;

  return {
    x: PIE_CHART_CENTER + Math.sin(radians) * PIE_CHART_RADIUS,
    y: PIE_CHART_CENTER - Math.cos(radians) * PIE_CHART_RADIUS,
  };
}

function getAdminKpisTrendWindowStartDate(
  latestSnapshotDate: string | undefined,
  selectedWindow: AdminKpisTrendWindow,
): string {
  if (!latestSnapshotDate) {
    return "";
  }

  const startDate = parseSnapshotDate(latestSnapshotDate);
  if (!startDate) {
    return latestSnapshotDate;
  }

  switch (selectedWindow) {
    case "30d":
      startDate.setUTCDate(startDate.getUTCDate() - 30);
      break;
    case "90d":
      startDate.setUTCDate(startDate.getUTCDate() - 90);
      break;
    case "180d":
      startDate.setUTCDate(startDate.getUTCDate() - 180);
      break;
    case "360d":
      startDate.setUTCDate(startDate.getUTCDate() - 360);
      break;
    default:
      break;
  }

  return toSnapshotDateKey(startDate);
}

function getAdminKpisTrendPointX(
  snapshotDate: string,
  pointIndex: number,
  pointCount: number,
  plotWidth: number,
  firstSnapshotTime: number | null,
  lastSnapshotTime: number | null,
): number {
  if (pointCount === 1) {
    return ADMIN_KPIS_TREND_PLOT_LEFT + plotWidth / 2;
  }

  const currentSnapshotTime = getSnapshotDateTime(snapshotDate);
  const snapshotTimeSpan =
    firstSnapshotTime !== null && lastSnapshotTime !== null ? lastSnapshotTime - firstSnapshotTime : 0;

  if (currentSnapshotTime !== null && firstSnapshotTime !== null && snapshotTimeSpan > 0) {
    return ADMIN_KPIS_TREND_PLOT_LEFT + ((currentSnapshotTime - firstSnapshotTime) / snapshotTimeSpan) * plotWidth;
  }

  return ADMIN_KPIS_TREND_PLOT_LEFT + (pointIndex / (pointCount - 1)) * plotWidth;
}

function buildSmoothTrendPath(points: AdminKpisTrendChartData["points"]): string {
  if (points.length === 0) {
    return "";
  }

  if (points.length === 1) {
    return `M ${points[0].x} ${points[0].y}`;
  }

  const pathCommands = [`M ${points[0].x} ${points[0].y}`];

  points.slice(1).forEach((point, index) => {
    const previousPoint = points[index];
    const controlX = roundChartCoordinate((previousPoint.x + point.x) / 2);
    pathCommands.push(`C ${controlX} ${previousPoint.y}, ${controlX} ${point.y}, ${point.x} ${point.y}`);
  });

  return pathCommands.join(" ");
}

function getAdminKpisTrendTooltipPoints(
  points: AdminKpisTrendChartData["points"],
  selectedWindow: AdminKpisTrendWindow,
): AdminKpisTrendChartData["points"] {
  const tooltipStride = getAdminKpisTrendTooltipStride(selectedWindow);
  const tooltipPoints = points.filter((_, index) => index % tooltipStride === 0);
  const endpoint = points[points.length - 1];

  if (endpoint && tooltipPoints[tooltipPoints.length - 1]?.snapshotDate !== endpoint.snapshotDate) {
    return [...tooltipPoints, endpoint];
  }

  return tooltipPoints;
}

function getAdminKpisTrendDateAxisPoints(
  tooltipPoints: AdminKpisTrendChartData["points"],
): AdminKpisTrendChartData["points"] {
  return tooltipPoints.filter((_, index) => index % 2 === 0);
}

function getAdminKpisTrendTooltipStride(selectedWindow: AdminKpisTrendWindow): number {
  switch (selectedWindow) {
    case "90d":
      return 3;
    case "180d":
      return 6;
    case "360d":
      return 12;
    case "30d":
    default:
      return 1;
  }
}

function getAdminKpisTrendChartWidth(requestedChartWidth: number): number {
  if (!Number.isFinite(requestedChartWidth)) {
    return ADMIN_KPIS_TREND_CHART_WIDTH;
  }

  return Math.max(Math.round(requestedChartWidth), ADMIN_KPIS_TREND_MIN_CHART_WIDTH);
}

function roundChartCoordinate(value: number): number {
  return Number(value.toFixed(2));
}

function getSnapshotDateTime(snapshotDate: string): number | null {
  return parseSnapshotDate(snapshotDate)?.getTime() ?? null;
}

function parseSnapshotDate(snapshotDate: string): Date | null {
  const [year, month, day] = snapshotDate.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day));
}

function toSnapshotDateKey(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function createAdminKpisPrintPageStyle(): HTMLStyleElement {
  document.getElementById(ADMIN_KPIS_PRINT_PAGE_STYLE_ID)?.remove();

  const styleElement = document.createElement("style");
  styleElement.id = ADMIN_KPIS_PRINT_PAGE_STYLE_ID;
  styleElement.media = "print";
  styleElement.textContent = ADMIN_KPIS_PRINT_PAGE_STYLE;
  document.head.appendChild(styleElement);

  return styleElement;
}
